import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Phone, Globe, MapPin, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";
import { SERVICE_OPTIONS } from "../../lib/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialState = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service_interest: "",
  message: "",
};

export default function Contact() {
  const { t, lang } = useLang();
  const f = t.contact.form;
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = f.required;
    if (!form.email.trim()) e.email = f.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = f.invalidEmail;
    if (!form.message.trim()) e.message = f.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      const payload = {
        name: form.name.trim(),
        company: form.company.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service_interest: form.service_interest || null,
        message: form.message.trim(),
      };
      await axios.post(`${API}/contacts`, payload, { timeout: 20000 });
      setStatus("success");
      setForm(initialState);
    } catch (err) {
      console.error("contact submit error", err);
      setStatus("error");
    }
  };

  const inputBase =
    "w-full h-12 px-4 bg-white border rounded-sm text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1E5BD6]/30 focus:border-[#1E5BD6] transition-colors";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-[#1E5BD6]">
              <span className="h-px w-8 bg-[#1E5BD6]" />
              {t.contact.label}
            </div>
            <h2
              data-testid="contact-title"
              className="mt-5 font-heading font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]"
            >
              {t.contact.title}
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-md">
              {t.contact.subtitle}
            </p>

            <ul className="mt-10 space-y-6">
              {[
                { icon: Mail, label: t.contact.info.email, value: "info@jaytech.id", href: "mailto:info@jaytech.id" },
                { icon: Phone, label: t.contact.info.phone, value: "+62 817 872 707", href: "tel:+62817872707" },
                { icon: Globe, label: t.contact.info.web, value: "jaytech.id", href: "https://jaytech.id" },
                { icon: MapPin, label: t.contact.info.address, value: t.contact.info.addressVal, href: null },
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-4" data-testid={`contact-info-${i}`}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-blue-50 text-[#1E5BD6] flex-shrink-0">
                    <item.icon className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-slate-500">
                      {item.label}
                    </div>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.href.startsWith("http") ? "_blank" : undefined}
                        rel="noreferrer"
                        className="mt-1 inline-block font-heading font-bold text-slate-900 hover:text-[#1E5BD6] transition-colors"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <div className="mt-1 font-heading font-bold text-slate-900">{item.value}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="relative bg-white border border-slate-200 rounded-sm p-6 sm:p-10 corner-accent"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-1">
                  <label className="block text-xs font-semibold text-slate-700 mb-2">
                    {f.name} *
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    data-testid="form-name"
                    className={`${inputBase} ${errors.name ? "border-red-400" : "border-slate-300"}`}
                    autoComplete="name"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-xs font-semibold text-slate-700 mb-2">
                    {f.company}
                  </label>
                  <input
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    data-testid="form-company"
                    className={`${inputBase} border-slate-300`}
                    autoComplete="organization"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-xs font-semibold text-slate-700 mb-2">
                    {f.email} *
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="form-email"
                    className={`${inputBase} ${errors.email ? "border-red-400" : "border-slate-300"}`}
                    autoComplete="email"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-xs font-semibold text-slate-700 mb-2">
                    {f.phone}
                  </label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="form-phone"
                    className={`${inputBase} border-slate-300`}
                    autoComplete="tel"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-2">
                    {f.service}
                  </label>
                  <select
                    name="service_interest"
                    value={form.service_interest}
                    onChange={onChange}
                    data-testid="form-service"
                    className={`${inputBase} border-slate-300 appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_1rem_center] bg-[length:10px]`}
                  >
                    <option value="">{f.servicePlaceholder}</option>
                    {SERVICE_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {lang === "id" ? opt.label_id : opt.label_en}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-2">
                    {f.message} *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={5}
                    placeholder={f.messagePlaceholder}
                    data-testid="form-message"
                    className={`w-full px-4 py-3 bg-white border rounded-sm text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1E5BD6]/30 focus:border-[#1E5BD6] resize-y ${
                      errors.message ? "border-red-400" : "border-slate-300"
                    }`}
                  />
                  {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                <p className="text-xs text-slate-500">
                  * {f.required}
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  data-testid="form-submit"
                  className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-sm bg-[#1E5BD6] hover:bg-[#164AA8] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold tracking-wide transition-colors"
                >
                  {status === "submitting" ? f.submitting : f.submit}
                  <Send className="h-4 w-4" />
                </button>
              </div>

              {status === "success" && (
                <div
                  data-testid="form-success"
                  className="mt-6 flex items-start gap-3 p-4 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-900"
                >
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-sm">{f.successTitle}</div>
                    <div className="text-xs mt-0.5 text-emerald-800">{f.successDesc}</div>
                  </div>
                </div>
              )}
              {status === "error" && (
                <div
                  data-testid="form-error"
                  className="mt-6 flex items-start gap-3 p-4 rounded-sm bg-red-50 border border-red-200 text-red-900"
                >
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-sm">{f.errorTitle}</div>
                    <div className="text-xs mt-0.5 text-red-800">{f.errorDesc}</div>
                  </div>
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
